import React from 'react';
import { ChartBar } from '@phosphor-icons/react';

interface KPIData {
    totalLearners?: number;
    totalEnrolled?: number;
    completionRate?: number;
    totalTimeHours?: number;
    avgProgress?: number;
}

interface OrgKPISummaryWidgetProps {
    data?: KPIData[] | null;
    loading?: boolean;
    error?: string;
}

const extractRow = (data: OrgKPISummaryWidgetProps['data']): KPIData | null => {
    if (!data) return null;
    if (Array.isArray(data)) return data[0] || null;
    return data as unknown as KPIData;
};

interface StatCardProps {
    value: string;
    label: string;
    bgClass: string;
    textClass: string;
}

const StatCard: React.FC<StatCardProps> = ({ value, label, bgClass, textClass }) => (
    <div className={`text-center p-4 ${bgClass} rounded-lg`}>
        <div className={`text-3xl font-bold ${textClass}`}>{value}</div>
        <div className="text-[11px] text-gray-500 mt-1">{label}</div>
    </div>
);

export const OrgKPISummaryWidget: React.FC<OrgKPISummaryWidgetProps> = ({ data, loading, error }) => {
    const row = extractRow(data);

    return (
        <div className="flex flex-col h-full p-5">
            <div className="flex items-center justify-between mb-4">
                <span className="text-xs text-gray-400">Organization-wide · All time</span>
            </div>
            <div className="flex-1">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : !row ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <ChartBar className="w-8 h-8" />
                        <span className="text-sm">No KPI data</span>
                    </div>
                ) : (
                    <div className="grid grid-cols-5 gap-4">
                        <StatCard value={String(Number(row.totalLearners) || 0)} label="Total Learners" bgClass="bg-primary/5" textClass="text-primary" />
                        <StatCard value={String(Number(row.totalEnrolled) || 0)} label="Total Enrollments" bgClass="bg-blue-50" textClass="text-blue-600" />
                        <StatCard value={`${Number(row.completionRate) || 0}%`} label="Completion Rate" bgClass="bg-emerald-50" textClass="text-emerald-600" />
                        <StatCard value={`${Number(row.totalTimeHours) || 0}h`} label="Avg Time to Complete" bgClass="bg-amber-50" textClass="text-amber-600" />
                        <StatCard value={`${Number(row.avgProgress) || 0}%`} label="Avg Progress" bgClass="bg-purple-50" textClass="text-purple-600" />
                    </div>
                )}
            </div>
        </div>
    );
};
